import {useState} from 'react'
import Script from 'next/script'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {z} from 'zod'
import {Button} from '@/products/flexible-web-toolkit/components/button/button'
import {pushToDataLayer} from '@/utilities/analytics'
import type {Block} from '@/services/kontent-ai/types'
import {logError, logMessage} from '@/services/logger/client'
import {AttemptedCodeInjectionError} from '@/utilities/exceptions'

export type ClickToBuyMultiProductContentItem = IContentItem<{
	dataKey: Elements.TextElement
	srcUrl: Elements.TextElement
	text: Elements.TextElement
}>

export const ClickToBuyMultiProduct: Block<
	ClickToBuyMultiProductContentItem
> = ({
	block: {
		elements: {srcUrl, dataKey, text},
	},
}) => {
	const [clickToBuyLoaded, setClickToBuyLoaded] = useState(false)

	const showClickToBuyPopup = (
		e: React.SyntheticEvent<EventTarget>
	): void => {
		e.preventDefault()
		logMessage(
			`From Smart Commerce - Click-to-buy-multi-product, Button clicked with name: ${text}`
		)
		pushToDataLayer({
			event: 'cta_click',
			cta_name: text,
			cta_category: 'CTA_Button',
		})
		if (typeof window !== 'undefined' && clickToBuyLoaded) {
			const widget = document.querySelector<HTMLDivElement>('.C2BC_popin')
			if (widget) {
				logMessage(
					'Smart Commerce - Click-to-buy-multi-product widget displayed'
				)
				widget.style.top = '30px'
				widget.style.display = 'block'
			} else {
				logMessage(
					'Smart Commerce - Click-to-buy-multi-product widget not found in the DOM'
				)
			}
		} else {
			logMessage(
				'Smart Commerce - Click-to-buy-multi-product not loaded yet'
			)
		}
	}

	const initClickToBuy = (): void => {
		setClickToBuyLoaded(true)
		logMessage(
			'Smart Commerce - Click-to-buy-multi-product widget initialized'
		)

		if (typeof window !== 'undefined') {
			window.c2bPopin?.init()
		} else {
			logMessage(
				'Window object not available for Smart Commerce - Click-to-buy-multi-product initialization'
			)
		}
	}

	// As the user can provide **any** src url, add safeguarding to ensure they can't code inject
	// This isn't ideal, but saves doing CMS migrations
	const requiredHostname = 'brands.click2buy.com'
	const isSrcUrl = z.string().url().safeParse(srcUrl)
	const url = isSrcUrl.success ? new URL(srcUrl) : null
	if (url && url.hostname !== requiredHostname) {
		logError(new AttemptedCodeInjectionError(srcUrl, requiredHostname))
		return null
	}

	return (
		<>
			<Script
				async
				id="click-to-buy-script-multi"
				onReady={() => {
					logMessage(
						'Smart Commerce - Click-to-buy-multi-product script loaded and ready'
					)
					initClickToBuy()
				}}
				src={srcUrl}
			/>
			<div
				className="btn C2B_Widget_Key my-2 text-sm md:my-0"
				data-key={dataKey}
				role="button"
			>
				<Button onClick={showClickToBuyPopup} padding="px-6 py-2">
					{text}
				</Button>
			</div>
		</>
	)
}
